/* DON'T EDIT THIS FILE: edit original and run build again */ import { ElementOf } from "../../framework/core/element-of.ts";
import { MaybeArray } from "../../framework/core/maybe-array.ts";
import { IdType } from "../../framework/core/type-id.ts";

export enum TypeCheckerErrorType {
  schemaFail = "schemaFail",
  invalidSchema = "invalidSchema",
  hadUnexpectedContent = "hadUnexpectedContent",
  unexpectedValue = "unexpectedValue",
  skippedInvalid = "skippedInvalid",
  caughtError = "caughtError",
}

export type TypeCheckerError = {
  type: TypeCheckerErrorType;
  path: string;
  value: unknown;
};

export enum TypeCheckerStatus {
  ok,
  fail,
}

export type TypeCheckerOkResult<T = unknown> = {
  status: TypeCheckerStatus.ok;
  value: T;
  errors: TypeCheckerError[];
};
export type TypeCheckerErrorResult = {
  status: TypeCheckerStatus.fail;
  errors: TypeCheckerError[];
};
export type TypeCheckerResult<T = unknown> =
  | TypeCheckerOkResult<T>
  | TypeCheckerErrorResult;

export enum TypeCheckerSchemaType {
  primitive,
  array,
  object,
  record,
}

export type TypeCheckerCatcher<T> = (
  value: unknown,
  path: string,
  result: TypeCheckerErrorResult
) => TypeCheckerResult<T>;

type CatchTypeCheckerOption<T> = {
  catch?: TypeCheckerCatcher<T>;
};

export type TypeCheckerPrimitiveTypes<T> =
  | (T extends number ? "number" : never)
  | (T extends string ? "string" : never)
  | (T extends boolean ? "boolean" : never)
  | (T extends null ? "null" : never);

export type TypeCheckerPrimitiveSchema<T = unknown> = {
  type: TypeCheckerSchemaType.primitive;
  schema: MaybeArray<TypeCheckerPrimitiveTypes<T>>;
  /**
   * validate custom format and maybe sanitize input
   */
  validator?: (value: unknown, path: string) => TypeCheckerResult<T>;
} & CatchTypeCheckerOption<T>;

export type TypeCheckerArraySchema<T = unknown> = {
  type: TypeCheckerSchemaType.array;
  schema: MaybeArray<TypeCheckerSchema<ElementOf<T>>>;
  skipInvalid?: boolean;
} & CatchTypeCheckerOption<T>;

export type TypeCheckerSchemaWithObjectEntryOptions<T> =
  TypeCheckerSchema<T> & {
    mapsFrom?: string;
  } & (undefined extends T ? { optional: true } : { optional?: false });

export type TypeCheckerObjectSchema<T = unknown> = {
  type: TypeCheckerSchemaType.object;
  schema: MaybeArray<{
    [K in keyof Required<T>]: MaybeArray<
      TypeCheckerSchemaWithObjectEntryOptions<T[K]>
    >;
  }>;
} & CatchTypeCheckerOption<T>;

export type TypeCheckerRecordSchema<T = unknown> = {
  type: TypeCheckerSchemaType.record;
  keysSchema: keyof NonNullable<T> extends string | number
    ? TypeCheckerPrimitiveSchema<keyof NonNullable<T>>
    : never;
  valuesSchema: MaybeArray<
    TypeCheckerSchema<
      NonNullable<T>[Extract<keyof NonNullable<T>, string | number>]
    >
  >;
  skipInvalid?: boolean;
} & CatchTypeCheckerOption<T>;

export type WithAnyTypeCheckerSchemaOptions<T> = T & {
  mapsFrom?: string;
  optional?: boolean;
  catch?: TypeCheckerCatcher<unknown>;
};

export type TypeCheckerRecurseChecker<T = unknown> = (
  checker: MaybeArray<TypeCheckerSchema<T>>,
  value: unknown,
  path: string
) => TypeCheckerResult<T>;

export type TypeCheckerSchema<T = unknown> =
  | TypeCheckerPrimitiveSchema<T>
  | TypeCheckerArraySchema<T>
  | TypeCheckerObjectSchema<T>
  | TypeCheckerRecordSchema<T>;

export type ValidatedTypeCheckerSchema<T> = IdType<
  "validTypeChecker",
  TypeCheckerSchema<T>
>;
